import Button from '@oncolens/oncolens-ui/lib/components/FormControls/Button';
import Combobox from '@oncolens/oncolens-ui/lib/components/FormControls/Combobox/Combobox';
import { ListItem } from '@oncolens/oncolens-ui/lib/components/FormControls/types';
import { CancerConfig } from '@oncolens/oncolens-ui/lib/dto/config/Config';
import FormField from '@oncolens/oncolens-ui/lib/libraries/FormField';
import { CreatedFormStore } from '@oncolens/oncolens-ui/lib/libraries/FormStore';
import React from 'react';
import { BsDashCircle } from 'react-icons/bs';
import { GuidelineTemplateConditionDto } from '../api/guideline-template';
import { GuidelineTemplateFormValues } from '../routes/ManageGuidelineTemplate.hooks';

type GuidelineTemplateConditionItemProps = {
  value: GuidelineTemplateConditionDto;
  onChange: (newValue: GuidelineTemplateConditionDto) => void;
  cancerConfigs: CancerConfig[];
};

function GuidelineTemplateConditionItem({
  value,
  onChange,
  cancerConfigs,
}: GuidelineTemplateConditionItemProps) {
  const selectedCancerIdItem = cancerConfigs.find(
    (cancerConfig) => cancerConfig.value === value.cancerTypeId,
  );
  const locationOptions: ListItem[] = selectedCancerIdItem?.locations || [];

  const selectedLocationItem = locationOptions.find(
    (locationConfig) => locationConfig.value === value.cancerLocationId,
  );
  const typeOptions: ListItem[] = selectedCancerIdItem?.pathologies || [];

  const selectedTypeItem = typeOptions.find(
    (locationConfig) => locationConfig.value === value.cancerPathologyId,
  );

  return (
    <div className="flex flex-1 space-x-4">
      <Combobox
        id="guideline-template-condition-cancer-id"
        htmlName="guideline-template-condition-cancer-id"
        wrapperProps={{
          className: 'w-full',
        }}
        label="Cancer"
        data={cancerConfigs}
        value={selectedCancerIdItem as ListItem}
        onChange={(selectedValue) => {
          onChange({
            ...value,
            cancerTypeId: selectedValue ? (selectedValue as ListItem).value : undefined,
            cancerLocationId: undefined,
            cancerPathologyId: undefined,
          });
        }}
        required
        useMultipleSelect={false}
        testSelector="combobox-cancer"
        useAutocomplete
      />
      <Combobox
        id="guideline-template-condition-location"
        htmlName="guideline-template-condition-location"
        wrapperProps={{
          className: 'w-full',
        }}
        label="Location"
        data={locationOptions}
        value={selectedLocationItem as ListItem}
        onChange={(selectedValue) => {
          onChange({
            ...value,
            cancerLocationId: selectedValue ? (selectedValue as ListItem).value : undefined,
          });
        }}
        useMultipleSelect={false}
        disabled={!selectedCancerIdItem}
        testSelector="combobox-location"
        useAutocomplete
      />
      <Combobox
        id="guideline-template-condition-type"
        htmlName="guideline-template-condition-type"
        wrapperProps={{
          className: 'w-full',
        }}
        label="Type"
        data={typeOptions}
        value={selectedTypeItem as ListItem}
        onChange={(selectedValue) => {
          onChange({
            ...value,
            cancerPathologyId: selectedValue ? (selectedValue as ListItem).value : undefined,
          });
        }}
        useMultipleSelect={false}
        disabled={!selectedCancerIdItem}
        testSelector="combobox-type"
        useAutocomplete
      />
    </div>
  );
}

type GuidelineTemplateConditionList = {
  cancerConfigs: CancerConfig[];
  formStore: CreatedFormStore<GuidelineTemplateFormValues>;
};

// eslint-disable-next-line @typescript-eslint/no-redeclare
export default function GuidelineTemplateConditionList({
  formStore,
  cancerConfigs,
}: GuidelineTemplateConditionList) {
  const [updateField, getValue] = formStore((x) => [x.updateField, x.getValue]);
  return (
    <div className="flex flex-col items-start space-y-2">
      <h2 className="text-xl font-medium text-blue-gray-900">Conditions</h2>
      <div className="flex-col w-full space-y-4">
        <FormField name="diseaseConditions" store={formStore}>
          {({ value, onChange }) =>
            (value as GuidelineTemplateConditionDto[]).map((item, index) => {
              const conditionsValue = value as GuidelineTemplateConditionDto[];
              return (
                <div
                  key={`guideline-template-condition-item-${index}`}
                  data-cy={`guideline-template-condition-item-${index}`}
                  className="flex items-end w-full space-x-4">
                  <GuidelineTemplateConditionItem
                    value={item}
                    onChange={(newValue) =>
                      // eslint-disable-next-line @typescript-eslint/no-shadow
                      onChange(conditionsValue.map((item, i) => (i === index ? newValue : item)))
                    }
                    cancerConfigs={cancerConfigs}
                  />
                  <div className="flex-0">
                    <Button
                      testSelector="delete-condition"
                      disabled={conditionsValue.length < 2}
                      onClick={() => {
                        onChange(conditionsValue.filter((_, i) => i !== index));
                      }}
                      buttonStyle="secondary"
                      className="border-none">
                      <BsDashCircle className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
              );
            })
          }
        </FormField>
        <Button
          testSelector="add-condition"
          onClick={() =>
            updateField('diseaseConditions', [
              ...getValue('diseaseConditions'),
              {
                cancerTypeId: undefined,
                cancerPathologyId: undefined,
                cancerLocationId: undefined,
              },
            ])
          }
          buttonStyle="secondary"
          buttonColor="blue"
          className="w-auto">
          + Add Condition
        </Button>
      </div>
    </div>
  );
}
